(function exposeTitanScanStateValidation(root, factory) {
  const defaultData =
    typeof module === 'object' && module.exports
      ? require('./tps-v2-data.js')
      : root.TitanScanData;
  const api = factory(defaultData);

  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.TitanScanStateValidation = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function createStateValidation(defaultData) {
  'use strict';

  const MIN_REACTION_MS = 1;
  const MAX_REACTION_MS = 1500;
  const MIN_BUDGET = 100000;
  const MAX_BUDGET = 350000;
  const BUDGET_STEP = 10000;
  const ALL_ANSWER_FIELDS = Object.freeze(['game', 'style', 'monitorHz', 'priority', 'streaming']);
  const SCREEN_REQUIREMENTS = Object.freeze({
    intro: Object.freeze({ answers: Object.freeze([]) }),
    game: Object.freeze({ answers: Object.freeze([]) }),
    profile: Object.freeze({ answers: Object.freeze(['game']) }),
    preferences: Object.freeze({ answers: Object.freeze(['game', 'style', 'monitorHz']) }),
    'input-intro': Object.freeze({ answers: ALL_ANSWER_FIELDS }),
    'input-test': Object.freeze({ answers: ALL_ANSWER_FIELDS }),
    'reaction-intro': Object.freeze({ answers: ALL_ANSWER_FIELDS, input: true }),
    'reaction-test': Object.freeze({ answers: ALL_ANSWER_FIELDS, input: true }),
    analyzing: Object.freeze({ answers: ALL_ANSWER_FIELDS, input: true, reaction: true }),
    result: Object.freeze({ answers: ALL_ANSWER_FIELDS, input: true, reaction: true }),
  });

  const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
  const includesValue = (collection, value) => Array.isArray(collection) && collection.includes(value);
  const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
  function safeInteger(value, fallback = 0) {
    const number = Number(value);
    return Number.isFinite(number) ? Math.max(0, Math.round(number)) : fallback;
  }
  function normalizeBudget(value) {
    const number = Number(value);
    const safeValue = Number.isFinite(number) ? number : 180000;
    return clamp(Math.round(safeValue / BUDGET_STEP) * BUDGET_STEP, MIN_BUDGET, MAX_BUDGET);
  }

  function sanitizeAnswers(rawAnswers, data = defaultData, allowPartial = true) {
    if (!isPlainObject(rawAnswers) || !data) return null;
    const gameIds = data.games.map((game) => game.id);
    const nullable = (value, allowed) => {
      if (value === null || typeof value === 'undefined') return allowPartial ? null : undefined;
      return includesValue(allowed, value) ? value : undefined;
    };
    const game = nullable(rawAnswers.game, gameIds);
    const style = nullable(rawAnswers.style, data.styles);
    const monitorValue =
      rawAnswers.monitorHz === null || typeof rawAnswers.monitorHz === 'undefined'
        ? rawAnswers.monitorHz
        : Number(rawAnswers.monitorHz);
    const monitorHz = nullable(monitorValue, data.monitorOptions);
    const priority = nullable(rawAnswers.priority, data.priorities);
    const streaming = nullable(rawAnswers.streaming, data.streamingOptions);
    const values = [game, style, monitorHz, priority, streaming];
    if (values.some((value) => typeof value === 'undefined')) return null;
    if (!allowPartial && values.some((value) => value === null)) return null;
    return {
      game,
      style,
      monitorHz,
      budget: normalizeBudget(rawAnswers.budget),
      budgetUnlimited: rawAnswers.budgetUnlimited === true,
      priority,
      streaming,
    };
  }

  function sanitizeMetrics(rawMetrics, data = defaultData) {
    if (!isPlainObject(rawMetrics) || !includesValue(data.inputModes, rawMetrics.mode)) return null;
    const reactions = Array.isArray(rawMetrics.reactions)
      ? rawMetrics.reactions
          .map(Number)
          .filter((sample) => Number.isFinite(sample) && sample >= MIN_REACTION_MS && sample <= MAX_REACTION_MS)
          .slice(0, 3)
          .map(Math.round)
      : [];
    const nullableInterval = (value) => {
      if (value === null || typeof value === 'undefined') return null;
      const interval = safeInteger(value);
      return interval >= 40 && interval <= 3000 ? interval : null;
    };
    const hits = clamp(safeInteger(rawMetrics.hits), 0, 999);
    const misses = clamp(safeInteger(rawMetrics.misses), 0, 999);
    const hasAttempts = hits + misses > 0;
    return {
      mode: rawMetrics.mode,
      hits,
      misses,
      accuracy: hasAttempts ? clamp(safeInteger(rawMetrics.accuracy), 0, 100) : 0,
      inputScore: hasAttempts ? clamp(safeInteger(rawMetrics.inputScore), 0, 100) : 0,
      averageInterval: nullableInterval(rawMetrics.averageInterval),
      fastestInterval: nullableInterval(rawMetrics.fastestInterval),
      reactions,
    };
  }

  function isProgressConsistent(screen, answers, metrics, completed, rawMetrics) {
    const requirements = SCREEN_REQUIREMENTS[screen];
    if (!requirements) return false;
    const hasRequiredAnswers = requirements.answers.every((field) => answers[field] !== null);
    const hasAllAnswers = ALL_ANSWER_FIELDS.every((field) => answers[field] !== null);
    const hasThreeReactions =
      Array.isArray(rawMetrics.reactions) && rawMetrics.reactions.length === 3 && metrics.reactions.length === 3;
    if (!hasRequiredAnswers) return false;
    if ((completed.input || completed.reaction) && !hasAllAnswers) return false;
    if (completed.reaction && (!completed.input || !hasThreeReactions)) return false;
    if (requirements.input && !completed.input) return false;
    if (requirements.reaction && (!completed.reaction || !hasThreeReactions)) return false;
    return true;
  }

  function validatePersistedState(rawState, data = defaultData) {
    if (!isPlainObject(rawState) || rawState.version !== data.schemaVersion || !includesValue(data.screens, rawState.screen)) {
      return null;
    }
    const answers = sanitizeAnswers(rawState.answers, data, true);
    const metrics = sanitizeMetrics(rawState.metrics, data);
    const mode = rawState.device && includesValue(data.inputModes, rawState.device.mode) ? rawState.device.mode : null;
    const override = rawState.device && includesValue(data.inputModes, rawState.device.override)
      ? rawState.device.override
      : null;
    const completed = {
      input: rawState.completed ? rawState.completed.input === true : false,
      reaction: rawState.completed ? rawState.completed.reaction === true : false,
    };
    const recommendedProductId = data.products.some((product) => product.id === rawState.recommendedProductId)
      ? rawState.recommendedProductId
      : null;
    if (
      !answers ||
      !metrics ||
      !mode ||
      metrics.mode !== mode ||
      (override !== null && override !== mode) ||
      !isProgressConsistent(rawState.screen, answers, metrics, completed, rawState.metrics)
    ) {
      return null;
    }
    return {
      version: data.schemaVersion,
      screen: rawState.screen,
      answers,
      device: { mode, override },
      metrics,
      recommendedProductId,
      completed,
      preferences: { muted: rawState.preferences ? rawState.preferences.muted !== false : true },
    };
  }

  return Object.freeze({ sanitizeAnswers, sanitizeMetrics, validatePersistedState });
});
